import React from "react";
import {
  Users,
  AlertTriangle,
  Search,
  Package,
  DollarSign,
  Layers,
  Target,
  TrendingUp,
  Dna,
  Sparkles,
  Hash,
  Compass,
  Wand2,
} from "lucide-react";
import { Button } from "./ui/button";
import CopyButton from "./CopyButton";
import OfferProductBuilder from "./OfferProductBuilder";

const Section = ({ icon: Icon, label, step, children, copy }) => (
  <div
    className="rounded-2xl border border-white/10 bg-zinc-900/70 p-6 fade-up transition-all duration-300 hover:border-amber-500/40"
    data-testid={`offer-section-${step}`}
  >
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-center gap-3">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30">
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
            Step {String(step).padStart(2, "0")}
          </div>
          <div className="font-display text-lg font-bold text-white -mt-0.5">
            {label}
          </div>
        </div>
      </div>
      {copy && <CopyButton text={copy} size="xs" label="Copy" />}
    </div>
    <div className="text-[14.5px] text-zinc-300 leading-relaxed space-y-2.5">
      {children}
    </div>
  </div>
);

const Field = ({ k, v }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-0.5">
      {k}
    </div>
    <div className="text-zinc-200">{v}</div>
  </div>
);

export const OfferOutput = ({ data, apiKey, onUseInContent }) => {
  if (!data) return null;

  const price = (n) => `$${Number(n).toLocaleString()}`;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-400">
          Niche
        </div>
        <div className="font-display text-xl sm:text-2xl font-bold text-white mt-1">
          {data.niche || "—"}
        </div>
        {data.trending_signals_used?.length ? (
          <div className="mt-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-2 inline-flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3 text-emerald-400" strokeWidth={2} />
              Trending signals used
            </div>
            <ul className="space-y-1">
              {data.trending_signals_used.map((s, i) => (
                <li key={i} className="text-xs text-zinc-400 flex items-start gap-2">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-emerald-400 shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <Section
        icon={Users}
        step={1}
        label="Target Audience"
        copy={`Who: ${data.target_audience?.who}\nLifestyle: ${data.target_audience?.lifestyle}`}
      >
        <Field k="Who they are" v={data.target_audience?.who} />
        <Field k="Lifestyle / situation" v={data.target_audience?.lifestyle} />
      </Section>

      <Section
        icon={AlertTriangle}
        step={2}
        label="Core Problem"
        copy={`Pain: ${data.core_problem?.pain_point}\nFrustration: ${data.core_problem?.emotional_frustration}`}
      >
        <Field k="Pain point" v={data.core_problem?.pain_point} />
        <Field k="Emotional frustration" v={data.core_problem?.emotional_frustration} />
      </Section>

      <Section
        icon={Search}
        step={3}
        label="What They're Searching For"
        copy={`Intent: ${data.searching_for?.intent}\nKeywords: ${(data.searching_for?.keywords || []).join(", ")}`}
      >
        <Field k="Real intent" v={data.searching_for?.intent} />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-2">
            Real search queries / topics
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(data.searching_for?.keywords || []).map((k, i) => (
              <span
                key={i}
                className="rounded-full border border-white/10 bg-zinc-950 px-2.5 py-1 text-xs text-zinc-300"
              >
                {k}
              </span>
            ))}
          </div>
        </div>
      </Section>

      <Section
        icon={Package}
        step={4}
        label="Digital Product"
        copy={`${data.digital_product?.idea} — Format: ${data.digital_product?.format}\nPositioning: ${data.digital_product?.positioning_and_packaging || ""}${data.positioning_statement ? `\n\nPositioning Statement: ${data.positioning_statement}` : ""}`}
      >
        <Field k="Idea" v={data.digital_product?.idea} />
        <Field k="Format" v={data.digital_product?.format} />
        {data.digital_product?.positioning_and_packaging && (
          <Field k="How to position & package" v={data.digital_product.positioning_and_packaging} />
        )}
        {data.positioning_statement && (
          <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 mt-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-violet-300 mb-1.5 inline-flex items-center gap-1.5">
              <Target className="h-3 w-3" strokeWidth={2} /> Positioning Statement
            </div>
            <p className="font-display text-lg text-white leading-snug italic">
              “{data.positioning_statement}”
            </p>
          </div>
        )}
      </Section>

      <Section
        icon={DollarSign}
        step={5}
        label="Pricing Strategy"
        copy={JSON.stringify(data.pricing, null, 2)}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { tier: "Low Ticket", data: data.pricing?.low_ticket, accent: "text-emerald-400", ring: "ring-emerald-500/30" },
            { tier: "Premium", data: data.pricing?.premium, accent: "text-amber-400", ring: "ring-amber-500/30" },
            { tier: "High Ticket", data: data.pricing?.high_ticket, accent: "text-rose-400", ring: "ring-rose-500/30" },
          ].map((row, i) => (
            <div key={i} className={`rounded-xl bg-zinc-950 p-4 ring-1 ${row.ring}`}>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
                {row.tier}
              </div>
              <div className={`font-display text-2xl font-black ${row.accent} mt-1`}>
                {row.data?.price_usd != null ? price(row.data.price_usd) : "—"}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1 leading-snug">
                {row.data?.note}
              </div>
            </div>
          ))}
        </div>
        <Field k="Justification" v={data.pricing?.justification} />
      </Section>

      <Section
        icon={Layers}
        step={6}
        label="Offer Ladder"
        copy={Object.entries(data.offer_ladder || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join("\n")}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ["Free Lead Magnet", data.offer_ladder?.free_lead_magnet],
            ["Low-Ticket", data.offer_ladder?.low_ticket],
            ["Premium", data.offer_ladder?.premium],
            ["High-Ticket", data.offer_ladder?.high_ticket],
          ].map(([k, v], i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-zinc-950 p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-400 mb-1">
                {k}
              </div>
              <div className="text-sm text-zinc-200 leading-relaxed">{v}</div>
            </div>
          ))}
        </div>
      </Section>

      <OfferProductBuilder apiKey={apiKey} offerData={data} />

      {data.audience_dna && (
        <AudienceDNASection
          dna={data.audience_dna}
          offerData={data}
          onUseInContent={onUseInContent}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// TARGET AUDIENCE DNA — generated alongside the offer in
// buildOfferFromIdeaPrompt. Renders 4 sub-sections inside a single
// "Step 7" card so it slots into the existing OfferOutput flow without
// changing layout/structure.
// ─────────────────────────────────────────────────────────────────────
const DNA_SUB_ICONS = {
  audience_overview:      Users,
  creator_ecosystem:      Sparkles,
  viral_content_insights: TrendingUp,
  seo_discovery:          Compass,
};

const DNA_SUB_LABELS = {
  audience_overview:      "Audience Overview",
  creator_ecosystem:      "Creator Ecosystem",
  viral_content_insights: "Viral Content Insights",
  seo_discovery:          "SEO + Discovery Insights",
};

const DnaList = ({ label, items }) => {
  if (!items || (Array.isArray(items) && items.length === 0)) return null;
  const list = Array.isArray(items) ? items : [items];
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1.5">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {list.map((it, i) => (
          <span
            key={i}
            className="rounded-full border border-white/10 bg-zinc-950 px-2.5 py-1 text-xs text-zinc-300"
          >
            {String(it)}
          </span>
        ))}
      </div>
    </div>
  );
};

const DnaText = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-0.5">
        {label}
      </div>
      <div className="text-zinc-200 text-[13.5px] leading-relaxed">{value}</div>
    </div>
  );
};

// Render a sub-section (e.g. audience_overview) — auto-detects whether each
// field is a string (use DnaText) or an array (use DnaList) so the prompt
// can evolve its shape without breaking the renderer.
const DnaSubSection = ({ subKey, data }) => {
  if (!data || typeof data !== "object") return null;
  const Icon = DNA_SUB_ICONS[subKey] || Hash;
  const label = DNA_SUB_LABELS[subKey] || subKey;

  // Pretty-print field keys (e.g. "trending_search_phrases" → "Trending search phrases")
  const prettyKey = (k) =>
    k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30">
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </div>
        <div className="font-display text-[15px] font-bold text-white">{label}</div>
      </div>
      <div className="space-y-3.5">
        {Object.entries(data).map(([k, v]) =>
          Array.isArray(v) ? (
            <DnaList key={k} label={prettyKey(k)} items={v} />
          ) : (
            <DnaText key={k} label={prettyKey(k)} value={v} />
          )
        )}
      </div>
    </div>
  );
};

const AudienceDNASection = ({ dna, offerData = {}, onUseInContent }) => {
  if (!dna) return null;
  // Keep a stable rendering order regardless of model output key order.
  const order = [
    "audience_overview",
    "creator_ecosystem",
    "viral_content_insights",
    "seo_discovery",
  ];

  // Build the handoff payload for "Use this DNA in Content".
  // We compress the DNA into a single readable Audience-DNA blob so the
  // Content tab's existing audienceDNA textarea stays the source of truth
  // (no schema changes, no UI changes downstream).
  const buildHandoff = () => {
    const overview = dna.audience_overview || {};
    const ecosystem = dna.creator_ecosystem || {};
    const viral = dna.viral_content_insights || {};
    const seo = dna.seo_discovery || {};

    const lines = [];
    if (overview.demographics) lines.push(`AUDIENCE: ${overview.demographics}`);
    if (overview.content_behavior) lines.push(`BEHAVIOUR: ${overview.content_behavior}`);

    const flat = (arr, max = 6) =>
      Array.isArray(arr) ? arr.slice(0, max).join(", ") : "";

    const pushLine = (label, val) => {
      if (val) lines.push(`${label}: ${val}`);
    };

    pushLine("PAIN POINTS", flat(overview.pain_points));
    pushLine("FEARS", flat(overview.fears));
    pushLine("DESIRES", flat(overview.desires));
    pushLine("BUYING TRIGGERS", flat(overview.buying_triggers));

    pushLine("INSTAGRAM", flat(ecosystem.instagram_creators));
    pushLine("TIKTOK", flat(ecosystem.tiktok_creators));
    pushLine("YOUTUBE", flat(ecosystem.youtube_channels));
    pushLine("SUBREDDITS", flat(ecosystem.subreddits));
    pushLine("INFLUENCERS", flat(ecosystem.influencers));
    pushLine("COMMUNITIES", flat(ecosystem.communities));

    pushLine("TRENDING HOOKS", flat(viral.trending_hooks));
    pushLine("VIRAL FORMATS", flat(viral.viral_post_formats));
    pushLine("EMOTIONAL TRIGGERS", flat(viral.emotional_triggers));
    pushLine("FREQUENT PROBLEMS", flat(viral.frequent_problems));
    pushLine("HIGH-PERFORMING ANGLES", flat(viral.high_performing_angles));

    pushLine("SEARCH PHRASES", flat(seo.trending_search_phrases));
    pushLine("VIRAL KEYWORDS", flat(seo.viral_keywords));
    pushLine("LONG-TAIL KEYWORDS", flat(seo.long_tail_keywords, 8));
    pushLine("HASHTAGS", flat(seo.high_performing_hashtags, 10));
    pushLine("FREQUENT QUESTIONS", flat(seo.frequent_questions));
    if (seo.audience_language_patterns)
      lines.push(`LANGUAGE PATTERNS: ${seo.audience_language_patterns}`);

    return {
      niche: offerData?.niche || "",
      audience: offerData?.target_audience?.who || offerData?.target_audience?.lifestyle || "",
      product: offerData?.digital_product?.idea || offerData?.offer_ladder?.low_ticket || "",
      audienceDNA: lines.join("\n"),
      pain: offerData?.core_problem?.emotional_frustration || offerData?.core_problem?.pain_point || "",
      keywords: [
        ...(offerData?.searching_for?.keywords || []),
        ...(seo.long_tail_keywords || []).slice(0, 6),
      ],
      positioning: offerData?.positioning_statement || "",
    };
  };

  const handleUseInContent = () => {
    if (!onUseInContent) return;
    onUseInContent(buildHandoff());
  };

  return (
    <Section
      icon={Dna}
      step={7}
      label="Target Audience DNA"
      copy={JSON.stringify(dna, null, 2)}
    >
      {onUseInContent && (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-2xl border border-emerald-500/25 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent p-4">
          <div className="space-y-0.5">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300">
              Close the loop
            </div>
            <p className="text-sm text-zinc-200 leading-relaxed">
              Pre-load these hooks, long-tail keywords, and native vocabulary into the Content tab.
            </p>
          </div>
          <Button
            type="button"
            data-testid="use-dna-in-content-button"
            onClick={handleUseInContent}
            className="h-10 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#082015] font-semibold text-xs px-4 whitespace-nowrap shadow-[0_10px_28px_-10px_rgba(16,185,129,0.55)] transition-all hover:-translate-y-[1px]"
          >
            <Wand2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={2.4} />
            Use this DNA in Content
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {order
          .filter((k) => dna[k])
          .map((k) => (
            <DnaSubSection key={k} subKey={k} data={dna[k]} />
          ))}
      </div>
    </Section>
  );
};

export default OfferOutput;
